/* Illustrations: the portal's own surfaces at a slightly smaller scale. No gradients, no shadows beyond one soft lift. */

.figure {
  margin: 0;
  min-width: 0;
}

.drawn {
  cursor: default;
  pointer-events: none;
}

.cardTitle {
  font-weight: 700;
  font-size: var(--text-md);
  color: var(--text);
}

.meta {
  font-size: var(--text-sm);
  color: var(--text-secondary);
}

.note {
  margin-top: var(--space-3);
  font-size: var(--text-xs);
  color: var(--text-secondary);
}

/* Hero timeline */

.timelineFigure {
  background: var(--white);
  border: 1px solid var(--border);
  border-radius: var(--radius-panel);
  padding: var(--space-5);
  box-shadow: 0 12px 32px -20px rgba(18, 39, 73, 0.35);
}

.timeline {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.step {
  position: relative;
  padding-left: 28px;
  font-size: var(--text-ui);
  color: var(--text-body);
}

/* The line and the dot of each step. */
.step::before {
  content: '';
  position: absolute;
  left: 5px;
  top: 6px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--white);
  border: 2px solid var(--border-strong);
}

.step:not(:last-child)::after {
  content: '';
  position: absolute;
  left: 10px;
  top: 20px;
  bottom: calc(-1 * var(--space-4) + 4px);
  width: 2px;
  background: var(--border);
}

.stepTask::before {
  border-color: var(--rust);
}

.stepDone::before {
  background: var(--sky);
  border-color: var(--synced-fg);
}

.when {
  display: block;
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-secondary);
  margin-bottom: 2px;
}

.taskCard {
  margin-top: var(--space-2);
  border: 1px solid var(--border);
  border-left: 4px solid var(--pear-700);
  border-radius: var(--radius-row);
  padding: var(--space-3) var(--space-4);
  background: var(--mist);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.taskHead {
  display: flex;
  gap: var(--space-2);
  font-size: var(--text-xs);
  color: var(--text-secondary);
}

.taskApp {
  font-weight: 700;
  color: var(--navy);
}

.taskTitle {
  font-weight: 700;
  color: var(--text);
}

/* The one animation on the page: the held pill hands over to the synced pill once, then stays. */
.pillSwap {
  display: inline-grid;
  justify-items: start;
}

.pillFrom,
.pillTo {
  grid-area: 1 / 1;
}

.pillFrom {
  animation: fadeOut 400ms ease 2.2s forwards;
}

.pillTo {
  opacity: 0;
  animation: fadeIn 400ms ease 2.4s forwards;
}

@keyframes fadeOut {
  to {
    opacity: 0;
  }
}

@keyframes fadeIn {
  to {
    opacity: 1;
  }
}

/* Held records group */

.held {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.heldHead {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: var(--space-2);
}

.reason {
  font-size: var(--text-ui);
  color: var(--text-body);
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.replay {
  border-top: 1px solid var(--border);
  padding-top: var(--space-3);
}

/* Shadow report */

.shadow {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.counts {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-3);
}

@media (min-width: 640px) {
  .counts {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.count {
  border: 1px solid var(--border);
  border-radius: var(--radius-row);
  padding: var(--space-3);
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.countValue {
  font-family: var(--font-heading);
  font-weight: 600;
  font-size: var(--text-xl);
  color: var(--text);
}

.countValue[data-tone='synced'] {
  color: var(--synced-fg);
}

.countValue[data-tone='held'] {
  color: var(--held-fg);
}

.countLabel {
  font-size: var(--text-sm);
  color: var(--text-secondary);
}

.diff {
  border: 1px solid var(--border);
  border-radius: var(--radius-row);
  padding: var(--space-3) var(--space-4);
  background: var(--mist);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.diffRecord {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-ui);
  font-weight: 700;
}

.diffTable {
  font-size: var(--text-sm);
}

.diffTable th,
.diffTable td {
  text-align: left;
  padding: 6px 8px 6px 0;
  border-bottom: 1px solid var(--border);
  vertical-align: top;
}

.diffTable thead th {
  color: var(--text-secondary);
  font-weight: 700;
}

.diffTable tbody th {
  font-weight: 700;
}

/* Browser frame and the tenant portal inside it */

.browser {
  border: 1px solid var(--border);
  border-radius: var(--radius-panel);
  overflow: hidden;
  background: var(--white);
  box-shadow: 0 16px 40px -24px rgba(18, 39, 73, 0.4);
}

.browserBar {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: 10px 14px;
  background: var(--neutral-bg);
  border-bottom: 1px solid var(--border);
}

.dots {
  display: inline-flex;
  gap: 6px;
}

.dots span {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--border-strong);
}

.address {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--text-xs);
  color: var(--neutral-fg);
  background: var(--white);
  border-radius: var(--radius-pill);
  padding: 3px 12px;
}

.browserBody {
  background: var(--mist);
}

/* The sheet's portal mock: a 64px white header bar (tenant logo slot, page title, "Powered by" on the right), then a
   mist body of list-row cards. */
.portal {
  display: flex;
  flex-direction: column;
  min-height: 320px;
}

.portalBar {
  min-height: 64px;
  background: var(--white);
  border-bottom: 1px solid var(--border);
  padding: var(--space-2) var(--space-5);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2) var(--space-4);
}

.portalBarStart {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-1) var(--space-3);
  min-width: 0;
}

.tenant {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-weight: 700;
  font-size: var(--text-ui);
}

.tenantMark {
  display: inline-grid;
  place-items: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: var(--brand-fill);
  color: var(--brand-on);
  font-family: var(--font-heading);
  font-weight: 600;
}

.portalHeading {
  font-family: var(--font-heading);
  font-weight: 600;
  font-size: var(--text-md);
  color: var(--text);
}

.portalNav {
  list-style: none;
  margin: 0;
  padding: var(--space-2) var(--space-5) 0;
  background: var(--white);
  border-bottom: 1px solid var(--border);
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1);
  font-size: var(--text-sm);
  color: var(--text-secondary);
}

.portalNav li {
  padding: 6px 10px 8px;
  border-bottom: 3px solid transparent;
}

.portalNavActive {
  color: var(--text);
  font-weight: 700;
  border-bottom-color: var(--brand-fill) !important;
}

.portalMain {
  padding: var(--space-5);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
  flex: 1;
}

.mappingName {
  font-weight: 700;
  color: var(--brand-fill);
}

/* Demo Studio chat */

.studio {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  color: var(--text);
  box-shadow: 0 12px 32px -20px rgba(18, 39, 73, 0.35);
}

.studioTitle {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0 var(--space-2);
  font-family: var(--font-heading);
  font-weight: 600;
  font-size: var(--text-lg);
  color: var(--text);
}

/* The designer's chat at illustration scale: its turns and chips (features/demos/designer.module.css) and the "Your
   demo" card. */
.studio :is(p, span) {
  overflow-wrap: anywhere;
}

.studioPreview {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--border);
  border-radius: var(--radius-row);
  background: var(--mist);
}

.studioPreviewTitle {
  font-family: var(--font-heading);
  font-weight: 600;
  font-size: var(--text-md);
  color: var(--text);
}

.studioObjects {
  list-style: none;
  margin: 0;
  padding: 0;
}

.studioPreviewHead {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
}

.studioObjects > li {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 0 var(--space-3);
  padding: var(--space-2) 0;
  border-top: 1px solid var(--border-soft);
  font-size: var(--text-sm);
}

.studioObjectLabel {
  font-weight: 700;
  color: var(--text);
}

/* Chat transcript */

.chat {
  margin: 0;
  border: 1px solid var(--border);
  border-radius: var(--radius-panel);
  background: var(--white);
  padding: var(--space-5);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.chatLabel {
  align-self: flex-start;
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-fg);
  background: var(--neutral-bg);
  border-radius: var(--radius-pill);
  padding: 3px 10px;
}

.chatList {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.msgRep,
.msgFit {
  border-radius: var(--radius-row);
  padding: var(--space-3) var(--space-4);
  font-size: var(--text-ui);
  max-width: 92%;
}

.msgRep {
  align-self: flex-end;
  background: var(--pear-50);
}

.msgFit {
  align-self: flex-start;
  background: var(--mist);
  border: 1px solid var(--border);
}

.msgWho {
  display: block;
  font-size: var(--text-xs);
  font-weight: 700;
  color: var(--text-secondary);
  margin-bottom: 2px;
}
